import React, { useState, useEffect, useCallback } from 'react';
import { FiCheckCircle, FiDownload, FiEdit2, FiMail, FiSend } from 'react-icons/fi';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSession } from '../../contexts/SessionContext';
import { accionesDocumento } from '../../utils/accionesDocumento';
import { puede } from '../../utils/permisos';
import ModalEnviarEmailCliente from '../ui/ModalEnviarEmailCliente';
import DocumentoEliminar from '../ui/DocumentoEliminar';
import Presupuestos from '../../pages/Presupuestos';

function fmtFecha(f) {
  if (!f) return '—';
  return new Date(f).toLocaleDateString('es-ES');
}

function fmtEuro(v) {
  return Number(v || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
}

export default function FacturaSession({ sesion }) {
  const { usuario, empresa } = useAuth();
  const { cerrarSesion } = useSession();
  const [accionDocumento, setAccionDocumento] = useState(null);
  const [factura, setFactura] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [modalEmailDoc, setModalEmailDoc] = useState(null);
  const puedeEnviar = puede(usuario, 'presupuestos', 'editar') || puede(usuario, 'presupuestos', 'crear') || puede(usuario, 'clientes', 'enviar_email');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get(`/facturas/${sesion.entityId}`);
      setFactura(data);
    } catch {
      setError('No se pudo cargar la factura.');
    } finally {
      setCargando(false);
    }
  }, [sesion.entityId]);

  const abrirPDF = () => {
    api.get(`/facturas/${factura._id}/pdf`, { responseType: 'blob' })
      .then(({ data }) => window.open(URL.createObjectURL(data), '_blank'))
      .catch(() => setError('No se pudo generar el PDF'));
  };
  const abrirModalEmail = async () => {
    if (!puedeEnviar) return;
    let clienteObj = typeof factura.cliente === 'object' && factura.cliente ? factura.cliente : { nombre: 'Cliente', email: '', telefono: '' };
    const clienteId = factura.clienteId?._id || factura.clienteId || factura.cliente;

    if (clienteId && (!clienteObj.email || !clienteObj.nombre)) {
      try {
        const { data } = await api.get(`/clientes/${clienteId}`);
        clienteObj = data || clienteObj;
      } catch {}
    }

    const numeroVisible = String(factura.numeroCustom || factura.numero || '').trim();
    const cuerpoTexto =
      `Estimado/a ${clienteObj.nombre || 'cliente'},\n\n` +
      `Adjunto la factura con referencia ${numeroVisible || 'sin número'} para su revisión.\n\n` +
      `Puedes revisarla y responder cuando quieras.\n\n` +
      `Un saludo.`;

    setModalEmailDoc({
      cliente: clienteObj,
      asunto: `Factura ${numeroVisible || ''}`.trim(),
      cuerpoTexto,
      tipo: 'factura',
      documentoId: factura._id,
      documentoTipo: 'factura',
      numeroDocumento: numeroVisible,
      numeroCustom: factura.numeroCustom || '',
    });
  };

  const enviarVerifactu = async () => {
    if (!accionesDocumento(usuario, empresa, 'facturas', factura).verifactu) return;
    try {
      await api.post(`/facturas/${factura._id}/enviar-verifactu`);
      await cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo enviar la factura a Verifactu.');
    }
  };

  const marcarPagada = async () => {
    if (!accionesDocumento(usuario, empresa, 'facturas', factura).pagar) return;
    if (!window.confirm('¿Quieres marcar esta factura como pagada?')) return;
    if (!accionesDocumento(usuario, empresa, 'facturas', factura).pagar) return;
    try {
      await api.patch(`/facturas/${factura._id}/pago`, { estado: 'pagada', metodoPago: 'manual' });
      await cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo actualizar el estado de la factura.');
    }
  };

  useEffect(() => { cargar(); }, [cargar]);

  if (cargando) return <div className="session-content"><p className="empty">Cargando…</p></div>;
  if (error) return <div className="session-content"><div className="alert-error">{error}</div></div>;
  if (!factura) return null;

  const lineas = factura.lineas || [];
  const disponibles = accionesDocumento(usuario, empresa, 'facturas', factura);
  const clienteObj = typeof factura.cliente === 'object' && factura.cliente ? factura.cliente : { nombre: 'Cliente', email: '', telefono: '' };

  return (
    <div className="session-content">
      <div className="session-panels" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', marginBottom: 24, padding: 0 }}>
        <div className="session-panel">
          <div className="session-panel-title">Número</div>
          <div className="session-kpi">#{factura.numero || '—'}</div>
        </div>
        <div className="session-panel">
          <div className="session-panel-title">Fecha</div>
          <div className="session-kpi" style={{ fontSize: 18 }}>{fmtFecha(factura.fecha || factura.createdAt)}</div>
        </div>
        <div className="session-panel">
          <div className="session-panel-title">Estado</div>
          <div className="session-kpi" style={{ fontSize: 18 }}>{factura.estado || '—'}</div>
        </div>
        <div className="session-panel">
          <div className="session-panel-title">Total</div>
          <div className="session-kpi">{fmtEuro(factura.total)}</div>
        </div>
        <div className="session-panel">
          <div className="session-panel-title">Verifactu</div>
          <div className="session-kpi" style={{ fontSize: 16 }}>
            {factura.verifactu?.enviado
              ? `Enviada (${fmtFecha(factura.verifactu?.enviadoAt)})`
              : (factura.verifactu?.estado || 'pendiente')}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 18 }}>
        {puedeEnviar && <button type="button" className="btn btn-primary" onClick={abrirModalEmail} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <FiSend /> Enviar
        </button>}
        {disponibles.editar && <button type="button" className="btn" title="Editar observaciones" onClick={() => setAccionDocumento({ tipo: 'facturas', documento: factura, accion: 'editar' })}><FiEdit2 /> Editar observaciones</button>}
        <DocumentoEliminar tipo="facturas" documento={factura} onEliminado={() => cerrarSesion(sesion.id)} />
        <button type="button" className="btn" onClick={abrirPDF} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <FiDownload /> Descargar PDF
        </button>
        {disponibles.verifactu && (
          <button type="button" className="btn" onClick={enviarVerifactu} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <FiMail /> Enviar a Verifactu
          </button>
        )}
        {disponibles.pagar && (
          <button type="button" className="btn" onClick={marcarPagada} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <FiCheckCircle /> Marcar pagada
          </button>
        )}
      </div>

      {accionDocumento && <Presupuestos accionDocumento={accionDocumento} onCerrarAccion={() => setAccionDocumento(null)} onDocumentoActualizado={async () => { setAccionDocumento(null); await cargar(); }} onDocumentoEliminado={() => cerrarSesion(sesion.id)} />}
      {factura.cliente && (
        <p style={{ marginBottom: 16, fontSize: 14, color: 'var(--text-soft)' }}>
          <strong>Cliente:</strong> {clienteObj?.nombre || factura.cliente}
        </p>
      )}

      <div style={{ marginBottom: 16 }}>
        <strong>Observaciones</strong>
        <p style={{ whiteSpace: 'pre-wrap' }}>{(factura.observaciones ?? factura.notas ?? '') || 'Sin observaciones'}</p>
      </div>

      {lineas.length > 0 && (
        <div className="table-wrap" style={{ marginTop: 8 }}>
          <table className="data-table">
            <thead>
              <tr><th>Descripción</th><th>Cant.</th><th>Precio unit.</th><th>Total</th></tr>
            </thead>
            <tbody>
              {lineas.map((l, i) => (
                <tr key={i}>
                  <td>{l.descripcion || '—'}</td>
                  <td>{l.cantidad ?? '—'}</td>
                  <td>{fmtEuro(l.precioUnitario)}</td>
                  <td>{fmtEuro(l.total ?? (l.cantidad * l.precioUnitario))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 16px', fontSize: 15, gap: 24 }}>
            {factura.subtotal != null && <span>Subtotal: <strong>{fmtEuro(factura.subtotal)}</strong></span>}
            {factura.iva != null && <span>IVA: <strong>{fmtEuro(factura.iva)}</strong></span>}
            <span>Total: <strong>{fmtEuro(factura.total)}</strong></span>
          </div>
        </div>
      )}
      <ModalEnviarEmailCliente
        abierto={!!modalEmailDoc}
        onCerrar={() => setModalEmailDoc(null)}
        cliente={modalEmailDoc?.cliente}
        asuntoInicial={modalEmailDoc?.asunto}
        cuerpoTextoInicial={modalEmailDoc?.cuerpoTexto}
        tipoInicial={modalEmailDoc?.tipo}
        documentoId={modalEmailDoc?.documentoId}
        documentoTipo={modalEmailDoc?.documentoTipo}
        numeroDocumento={modalEmailDoc?.numeroDocumento}
        numeroCustom={modalEmailDoc?.numeroCustom}
        onEnviado={() => setModalEmailDoc(null)}
      />
    </div>
  );
}
